import { memo } from 'react';
import type { Url } from 'next/dist/shared/lib/router/router';
import Icon from '@/components/shared/Icons';
import { Link } from '@/lib/router-events/patch-router/link';

interface Props {
  ariaLabel?: string;
  accessible?: boolean;
  text?: string;
  disabled: boolean;
  selected?: boolean;
  pageUrl: Url;
  icon?: 'left' | 'right';
  dataCventId: string;
  onClick?: () => void;
}

function renderIcon(icon: 'left' | 'right' | undefined, disabled: boolean) {
  if (typeof icon === 'undefined') {
    return null;
  }
  return <Icon id={`chevron-${icon}`} className={`h-6 w-6 fill-${disabled ? 'neutral-30' : 'neutral-50'}`} />;
}

function getNonActiveItemStyles(icon: string | undefined, disabled: boolean | undefined): string {
  const iconSpecificStyles = icon ? 'p-3' : 'p-2';
  const disabledStyles = disabled
    ? 'pointer-events-none fill-neutral-50 text-neutral-50'
    : 'text-brand-60 focus:text-interactive-60 focus:outline-0.5 focus:outline-interactive-40';
  const nonActiveItemStyles = `bg-transparent mx-1 min-w-4 cursor-pointer appearance-none whitespace-nowrap rounded-xs border-solid ${iconSpecificStyles} text-center font-primary font-normal normal-case leading-tight tracking-normal ${disabledStyles} outline-none transition-shadow hover:bg-[#E7F2FF] hover:text-[#0050AB] active:bg-[#D3E5FA] active:text-[#003673]}`;
  return nonActiveItemStyles;
}

function getSelectedItemStyles(): string {
  const selectedItemStyles =
    'hover:bg-transparent bg-transparent mx-1 pointer-events-none relative min-w-4 cursor-pointer appearance-none whitespace-nowrap rounded-none border-solid p-2 text-center font-primary font-normal normal-case leading-tight tracking-normal text-neutral-90 outline-none transition-shadow before:absolute before:bottom-0 before:left-0 before:right-0 before:h-1 before:rounded-xl before:bg-accent-30 before:content-[""] disabled:pointer-events-none disabled:fill-neutral-40 disabled:text-neutral-90';
  return selectedItemStyles;
}
/**
 * Each item in the pagination range
 * @returns Element for Pagination Range
 */
function ItemElement({ ariaLabel, accessible, text, disabled, selected, pageUrl, icon, dataCventId, onClick }: Props) {
  return (
    <Link
      type="link"
      aria-hidden={!accessible}
      aria-label={ariaLabel}
      aria-disabled={disabled}
      className={selected ? getSelectedItemStyles() : getNonActiveItemStyles(icon, disabled)}
      href={pageUrl as string}
      aria-current={selected}
      data-cvent-id={dataCventId}
      tabIndex={disabled ? -1 : 0}
      onClick={onClick}
    >
      {renderIcon(icon, disabled)}
      {text}
    </Link>
  );
}

export default memo(ItemElement);
